// File: src/pages/CategorySubcategoryManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Label,
  Form,
  FormGroup,
  Spinner,
} from "reactstrap";
import { FaPlus, FaPencilAlt } from "react-icons/fa";
import { Trash2 } from "react-feather";
import swal from "sweetalert";
import * as XLSX from "xlsx";

// ✅ YOUR EXISTING CATEGORY/SUBCATEGORY API CALLS (unchanged)
import {
  AllCategoryList,
  CreateCategory,
  UpdateCategory,
  View_Catby_id,
  Delete_SubCategory,
  DeleteCategory,
  CreateSubCategory,
  Update_SubCategory, // keep your alias if needed
} from "../import/ApiEndPoint/ApiCalling";

// ✅ PRODUCT SECTION ENDPOINT CONSTANTS (used with fetch)
import {
  Create_ProductSection,
  ProductSection_List,
  View_ProductSection_by_id,
  Update_ProductSection,
  Delete_ProductSection,
  Image_URL,
} from "../import/ApiEndPoint/Api";

// ===== inline styles (no scss)
const injectOnce = () => {
  if (document.getElementById("cat-sub-inline-css")) return;
  const s = document.createElement("style");
  s.id = "cat-sub-inline-css";
  s.textContent = `
    .cs-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .cs-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .cs-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .cs-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:700px; }
    .cs-table thead th{ background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em; border-bottom:1px solid #e5e7eb; padding:6px 8px; text-align:center; white-space:nowrap; }
    .cs-table tbody td{ padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center; }
    .cs-table tbody tr:hover{ background:#f9fbff; }
    .actions{ display:flex; justify-content:center; align-items:center; gap:8px; }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer; }
    .chip-edit{ background:#d46f10; }
    .chip-del{ background:#ec1809; }
    .tab-btn{ height:32px; border-radius:14px; padding:0 12px; font-weight:700; border:1px solid #e5e7eb; background:#fff; }
    .tab-btn.active{ background:#0b5cf6; color:#fff; border-color:#0b5cf6; }
  `;
  document.head.appendChild(s);
};

// ===== utilities
const toStr = (v) => (v === null || v === undefined ? "" : String(v));

// --- safe URL join (removes duplicate slashes between parts)
const joinUrl = (...parts) =>
  parts
    .filter(Boolean)
    .map((p, i) =>
      String(p)
        [i === 0 ? "replace" : "replaceAll"](/\/+$/g, "")
        .replace(/^\/+/g, ""),
    )
    .join("/");

export default function CategorySubcategoryManager() {
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("categories"); // "categories" | "subcategories" | "products"
  const [search, setSearch] = useState("");

  // ===== Categories (from your APIs)
  const [rows, setRows] = useState([]); // [{_id,name,description,status,subcategories:[]}]
  const [showCreateCat, setShowCreateCat] = useState(false);
  const [catForm, setCatForm] = useState({
    name: "",
    description: "",
    status: "Active",
  });
  const [catFile, setCatFile] = useState(null);
  const [createCatLoading, setCreateCatLoading] = useState(false);

  const [editingCatId, setEditingCatId] = useState(null);
  const [editCat, setEditCat] = useState({
    name: "",
    description: "",
    _id: "",
  });
  const [editCatLoading, setEditCatLoading] = useState(false);

  // ===== Subcategories (from your APIs)
  const [showCreateSub, setShowCreateSub] = useState(false);
  const [subForm, setSubForm] = useState({
    categoryId: "",
    name: "",
    description: "",
    status: "Active",
  });
  const [createSubLoading, setCreateSubLoading] = useState(false);

  const [editingSub, setEditingSub] = useState(null); // {catId, subId, parentId, name, description, status}
  const [editSubLoading, setEditSubLoading] = useState(false);

  // ===== Product Sections (using your backend with fetch)
  const [productRows, setProductRows] = useState([]); // [{_id,name,description,status}]
  const [showCreateProd, setShowCreateProd] = useState(false);
  const [prodForm, setProdForm] = useState({
    name: "",
    description: "",
    status: "Active",
  });
  const [createProdLoading, setCreateProdLoading] = useState(false);
  const [editingProdId, setEditingProdId] = useState(null);
  const [editProd, setEditProd] = useState({
    name: "",
    description: "",
    _id: "",
  });
  const [editProdLoading, setEditProdLoading] = useState(false);

  useEffect(() => injectOnce(), []);

  // ===== load categories via your API + load product sections via your API =====
  const fetchData = async () => {
    setLoading(true);
    try {
      // Categories & Subcategories (your API)
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const res = await AllCategoryList(user?._id, user?.database);
      const list = Array.isArray(res?.Category) ? res.Category : [];
      setRows(list);
    } catch (e) {
      setRows([]);
    }

    // Product Sections (your backend via fetch)
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const url = joinUrl(
        Image_URL,
        ProductSection_List,
        user?._id,
        user?.database,
      );
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProductRows(
        Array.isArray(data?.ProductSection) ? data.ProductSection : [],
      );
    } catch (e) {
      console.error(e);
      setProductRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ===== sub rows flatten
  const subRows = useMemo(() => {
    const flat = [];
    for (const c of rows) {
      const subs = Array.isArray(c?.subcategories) ? c.subcategories : [];
      for (const s of subs) {
        flat.push({
          catId: c?._id,
          catName: c?.name,
          subId: s?._id,
          subName: s?.name,
          subDesc: s?.description || "",
          status: s?.status || c?.status || "Active",
        });
      }
    }
    return flat;
  }, [rows]);

  // ===== filters
  const term = search.trim().toLowerCase();
  const filteredCategories = useMemo(() => {
    if (!term) return rows;
    return rows.filter(
      (r) =>
        toStr(r?.name).toLowerCase().includes(term) ||
        toStr(r?.status).toLowerCase().includes(term) ||
        toStr(r?.description).toLowerCase().includes(term),
    );
  }, [rows, term]);

  const filteredSubs = useMemo(() => {
    if (!term) return subRows;
    return subRows.filter(
      (r) =>
        toStr(r?.catName).toLowerCase().includes(term) ||
        toStr(r?.subName).toLowerCase().includes(term) ||
        toStr(r?.status).toLowerCase().includes(term),
    );
  }, [subRows, term]);

  const filteredProducts = useMemo(() => {
    if (!term) return productRows;
    return productRows.filter(
      (r) =>
        toStr(r?.name).toLowerCase().includes(term) ||
        toStr(r?.status).toLowerCase().includes(term) ||
        toStr(r?.description).toLowerCase().includes(term),
    );
  }, [productRows, term]);

  // ===== Export XLSX
  const exportXLSX = () => {
    const data =
      mode === "categories"
        ? filteredCategories.map((c, i) => ({
            "S.NO": i + 1,
            "CATEGORY NAME": toStr(c?.name),
            STATUS: toStr(c?.status),
            DESCRIPTION: toStr(c?.description),
          }))
        : mode === "subcategories"
          ? filteredSubs.map((s, i) => ({
              "S.NO": i + 1,
              "CATEGORY NAME": toStr(s?.catName),
              "SUBCATEGORY NAME": toStr(s?.subName),
              STATUS: toStr(s?.status),
            }))
          : filteredProducts.map((p, i) => ({
              "S.NO": i + 1,
              "SECTION NAME": toStr(p?.name),
              STATUS: toStr(p?.status),
              DESCRIPTION: toStr(p?.description),
            }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      mode === "categories"
        ? "Categories"
        : mode === "subcategories"
          ? "Subcategories"
          : "Products",
    );
    XLSX.writeFile(
      wb,
      mode === "categories"
        ? "CategoryList.xlsx"
        : mode === "subcategories"
          ? "SubcategoryList.xlsx"
          : "ProductSections.xlsx",
    );
  };

  // ========== CATEGORY: CREATE (your API) ==========
  const onCreateCategory = async (e) => {
    e.preventDefault();
    setCreateCatLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const fd = new FormData();
      fd.append("created_by", user?._id || "");
      fd.append("name", (catForm.name || "").toUpperCase());
      fd.append("description", catForm.description || "");
      fd.append("status", catForm.status || "Active");
      if (catFile) fd.append("file", catFile);
      await CreateCategory(fd);
      swal("Success!", "Category Created", "success");
      setCatForm({ name: "", description: "", status: "Active" });
      setCatFile(null);
      setShowCreateCat(false);
      fetchData();
    } catch (err) {
      swal("Error", "Could not create category", "error");
    } finally {
      setCreateCatLoading(false);
    }
  };

  // ========== CATEGORY: EDIT (your API) ==========
  const startEditCategory = async (id) => {
    try {
      setEditingCatId(id);
      setEditCatLoading(true);
      const res = await View_Catby_id(id);
      const c = res?.Category || {};
      setEditCat({
        name: c?.name || "",
        description: c?.description || "",
        _id: c?._id,
      });
    } catch (err) {
      swal("Error", "Failed to load category", "error");
      setEditingCatId(null);
    } finally {
      setEditCatLoading(false);
    }
  };

  const onUpdateCategory = async (e) => {
    e.preventDefault();
    if (!editingCatId) return;
    setEditCatLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", editCat.name || "");
      fd.append("description", editCat.description || "");
      await UpdateCategory(editingCatId, fd);
      swal("Success!", "Category Updated", "success");
      setEditingCatId(null);
      fetchData();
    } catch (err) {
      swal("Something went Wrong");
    } finally {
      setEditCatLoading(false);
    }
  };

  const onDeleteCategory = (id) => {
    swal("Warning", "Sure You Want to Delete it", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then(async (value) => {
      if (value === "delete") {
        try {
          await DeleteCategory(id);
          swal("Success", "Category Deleted Successfully");
          fetchData();
        } catch (err) {
          swal("Error", "Some Error Occurred");
        }
      }
    });
  };

  // ========== SUBCATEGORY: CREATE (your API) ==========
  const onCreateSubcategory = async (e) => {
    e.preventDefault();
    if (!subForm.categoryId) return swal("Select a parent category");

    setCreateSubLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const fd = new FormData();
      fd.append("created_by", user?._id || "");
      fd.append("name", (subForm.name || "").toUpperCase());
      fd.append("category", subForm.categoryId);
      fd.append("description", subForm.description || "");
      fd.append("status", subForm.status || "Active");
      await CreateSubCategory(fd);
      swal("Success!", "Your Subcategory has been Added", "success");
      setSubForm({
        categoryId: "",
        name: "",
        description: "",
        status: "Active",
      });
      setShowCreateSub(false);
      fetchData();
    } catch (err) {
      swal("Error", "Could not create subcategory", "error");
    } finally {
      setCreateSubLoading(false);
    }
  };

  // ========== SUBCATEGORY: EDIT (your API) ==========
  const startEditSub = (row) => {
    setEditingSub({
      catId: row?.catId,
      subId: row?.subId,
      parentId: row?.catId,
      name: row?.subName || "",
      description: row?.subDesc || "",
      status: row?.status || "Active",
    });
    setTimeout(() => {
      const el = document.getElementById("edit-sub-card");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const onUpdateSub = async (e) => {
    e.preventDefault();
    if (!editingSub?.subId || !editingSub?.parentId) {
      return swal("Please select parent category and subcategory details");
    }
    setEditSubLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", (editingSub.name || "").toUpperCase());
      fd.append("description", editingSub.description || "");
      fd.append("status", editingSub.status || "Active");
      // if backend expects new parent also:
      fd.append("category", editingSub.parentId);

      await Update_SubCategory(editingSub.parentId, editingSub.subId, fd);

      swal("Success!", "Subcategory Updated", "success");
      setEditingSub(null);
      fetchData();
    } catch (err) {
      swal("Error", "Could not update subcategory", "error");
    } finally {
      setEditSubLoading(false);
    }
  };

  const onDeleteSub = (row) => {
    swal("Warning", "Sure You Want to Delete it", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then(async (value) => {
      if (value === "delete") {
        try {
          await Delete_SubCategory(row?.catId, row?.subId);
          swal("SubCategory Deleted Successfully");
          fetchData();
        } catch (err) {
          swal("Something went wrong");
        }
      }
    });
  };

  // ========== PRODUCT SECTIONS: CREATE / VIEW / UPDATE / DELETE (fetch) ==========
  const onCreateProductSection = async (e) => {
    e.preventDefault();
    setCreateProdLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const payload = {
        created_by: user?._id || "",
        database: user?.database || "",
        name: (prodForm.name || "").toUpperCase(),
        description: prodForm.description || "",
        status: prodForm.status || "Active",
      };

      const res = await fetch(joinUrl(Image_URL, Create_ProductSection), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.status === false) {
        throw new Error(json?.message || `HTTP ${res.status}`);
      }

      swal("Success!", "Section Created", "success");
      setProdForm({ name: "", description: "", status: "Active" });
      setShowCreateProd(false);
      fetchData();
    } catch (err) {
      console.error(err);
      swal("Error", "Could not create section", "error");
    } finally {
      setCreateProdLoading(false);
    }
  };

  const startEditProductSection = async (id) => {
    try {
      setEditingProdId(id);
      setEditProdLoading(true);
      const url = joinUrl(Image_URL, View_ProductSection_by_id, id);
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const p = data?.ProductSection || {};
      setEditProd({
        name: p?.name || "",
        description: p?.description || "",
        _id: p?._id || id,
      });
    } catch (err) {
      swal("Error", "Failed to load section", "error");
      setEditingProdId(null);
    } finally {
      setEditProdLoading(false);
    }
  };

  const onUpdateProductSection = async (e) => {
    e.preventDefault();
    if (!editingProdId) return;
    setEditProdLoading(true);
    try {
      const payload = {
        name: (editProd.name || "").toUpperCase(),
        description: editProd.description || "",
      };
      const url = joinUrl(Image_URL, Update_ProductSection, editingProdId);
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.status === false) {
        throw new Error(json?.message || `HTTP ${res.status}`);
      }

      swal("Success!", "Section Updated", "success");
      setEditingProdId(null);
      fetchData();
    } catch (err) {
      swal("Error", "Could not update section", "error");
    } finally {
      setEditProdLoading(false);
    }
  };

  const onDeleteProductSection = (id) => {
    swal("Warning", "Sure You Want to Delete it", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then(async (value) => {
      if (value === "delete") {
        try {
          const url = joinUrl(Image_URL, Delete_ProductSection, id); // backend uses GET for delete
          const res = await fetch(url, {
            method: "GET",
            headers: { Accept: "application/json" },
          });
          const json = await res.json().catch(() => ({}));
          if (!res.ok || json?.status === false) {
            throw new Error(json?.message || `HTTP ${res.status}`);
          }
          swal("Deleted", "Section deleted", "success");
          fetchData();
        } catch (err) {
          swal("Error", "Could not delete section", "error");
        }
      }
    });
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {/* ===== Toolbar ===== */}
      <Row className="cs-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="cs-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                {mode === "categories"
                  ? "Category List"
                  : mode === "subcategories"
                    ? "Subcategory List"
                    : "Product Sections"}{" "}
                <span className="count-pill">
                  {mode === "categories"
                    ? filteredCategories.length
                    : mode === "subcategories"
                      ? filteredSubs.length
                      : filteredProducts.length}
                </span>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className={`tab-btn ${mode === "categories" ? "active" : ""}`}
                  onClick={() => setMode("categories")}
                  type="button"
                >
                  Categories
                </button>
                <button
                  className={`tab-btn ${
                    mode === "subcategories" ? "active" : ""
                  }`}
                  onClick={() => setMode("subcategories")}
                  type="button"
                >
                  Subcategories
                </button>
                <button
                  className={`tab-btn ${mode === "products" ? "active" : ""}`}
                  onClick={() => setMode("products")}
                  type="button"
                >
                  Product Sections
                </button>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {mode === "categories" ? (
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                  }}
                  onClick={() => {
                    setShowCreateCat((s) => !s);
                    setEditingCatId(null);
                  }}
                >
                  <FaPlus size={12} />{" "}
                  {showCreateCat ? "Close" : "Add Category"}
                </Button>
              ) : mode === "subcategories" ? (
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                  }}
                  onClick={() => {
                    setShowCreateSub((s) => !s);
                    setEditingSub(null);
                  }}
                >
                  <FaPlus size={12} />{" "}
                  {showCreateSub ? "Close" : "Add Subcategory"}
                </Button>
              ) : (
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                  }}
                  onClick={() => {
                    setShowCreateProd((s) => !s);
                    setEditingProdId(null);
                  }}
                >
                  <FaPlus size={12} />{" "}
                  {showCreateProd ? "Close" : "Add Product Section"}
                </Button>
              )}

              <Button
                outline
                color="primary"
                style={{ height: 32 }}
                onClick={exportXLSX}
              >
                Export .XLSX
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Add Category ===== */}
      {mode === "categories" && showCreateCat && (
        <Row style={{ marginTop: 8 }}>
          <Col sm="12">
            <Card className="cs-card">
              <CardBody>
                <Form onSubmit={onCreateCategory}>
                  <Row>
                    <Col lg="4" md="6" sm="12">
                      <FormGroup>
                        <Label>Category Name *</Label>
                        <Input
                          required
                          placeholder="Category Name"
                          value={catForm.name}
                          onChange={(e) =>
                            setCatForm((p) => ({ ...p, name: e.target.value }))
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col lg="4" md="6" sm="12">
                      <FormGroup>
                        <Label>Image (optional)</Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) =>
                            setCatFile(e.target.files?.[0] || null)
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col lg="4" md="6" sm="12">
                      <FormGroup>
                        <Label>Status</Label>
                        <div className="d-flex align-items-center gap-3">
                          <label className="m-0 d-flex align-items-center gap-1">
                            <Input
                              type="radio"
                              name="status"
                              checked={catForm.status === "Active"}
                              onChange={() =>
                                setCatForm((p) => ({ ...p, status: "Active" }))
                              }
                            />
                            <span>Active</span>
                          </label>
                          <label className="m-0 d-flex align-items-center gap-1">
                            <Input
                              type="radio"
                              name="status"
                              checked={catForm.status === "Deactive"}
                              onChange={() =>
                                setCatForm((p) => ({
                                  ...p,
                                  status: "Deactive",
                                }))
                              }
                            />
                            <span>Deactive</span>
                          </label>
                        </div>
                      </FormGroup>
                    </Col>
                    <Col lg="12" md="12" sm="12">
                      <FormGroup>
                        <Label>Description</Label>
                        <Input
                          type="textarea"
                          rows={3}
                          placeholder="Description"
                          value={catForm.description}
                          onChange={(e) =>
                            setCatForm((p) => ({
                              ...p,
                              description: e.target.value,
                            }))
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      color="secondary"
                      type="button"
                      onClick={() => setShowCreateCat(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      type="submit"
                      disabled={createCatLoading}
                    >
                      {createCatLoading ? "Saving..." : "Save Category"}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* ===== Edit Category ===== */}
      {mode === "categories" && editingCatId && (
        <Row style={{ marginTop: 8 }}>
          <Col sm="12">
            <Card className="cs-card">
              <CardBody>
                <Form onSubmit={onUpdateCategory}>
                  <Row>
                    <Col lg="6" md="6" sm="12">
                      <FormGroup>
                        <Label>Category Name</Label>
                        <Input
                          placeholder="Category Name"
                          value={editCat.name}
                          onChange={(e) =>
                            setEditCat((p) => ({ ...p, name: e.target.value }))
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col lg="6" md="6" sm="12">
                      <FormGroup>
                        <Label>Description</Label>
                        <Input
                          placeholder="Description"
                          value={editCat.description}
                          onChange={(e) =>
                            setEditCat((p) => ({
                              ...p,
                              description: e.target.value,
                            }))
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end gap-2 mt-2">
                    <Button
                      color="secondary"
                      type="button"
                      onClick={() => setEditingCatId(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      type="submit"
                      disabled={editCatLoading}
                    >
                      {editCatLoading ? "Saving..." : "Update Category"}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* ===== Add Subcategory ===== */}
      {mode === "subcategories" && showCreateSub && (
        <Row style={{ marginTop: 8 }}>
          <Col sm="12">
            <Card className="cs-card">
              <CardBody>
                <Form onSubmit={onCreateSubcategory}>
                  <Row>
                    <Col lg="4" md="6" sm="12">
                      <FormGroup>
                        <Label>Parent Category *</Label>
                        <Input
                          type="select"
                          required
                          value={subForm.categoryId}
                          onChange={(e) =>
                            setSubForm((p) => ({
                              ...p,
                              categoryId: e.target.value,
                            }))
                          }
                        >
                          <option value="">-- Select Category --</option>
                          {rows.map((c) => (
                            <option key={c?._id} value={c?._id}>
                              {c?.name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col lg="4" md="6" sm="12">
                      <FormGroup>
                        <Label>Subcategory Name *</Label>
                        <Input
                          required
                          placeholder="Subcategory Name"
                          value={subForm.name}
                          onChange={(e) =>
                            setSubForm((p) => ({ ...p, name: e.target.value }))
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col lg="4" md="6" sm="12">
                      <FormGroup>
                        <Label>Subcategory Description</Label>
                        <Input
                          type="textarea"
                          rows={3}
                          placeholder="Description"
                          value={subForm.description}
                          onChange={(e) =>
                            setSubForm((p) => ({
                              ...p,
                              description: e.target.value,
                            }))
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col lg="4" md="6" sm="12">
                      <FormGroup>
                        <Label>Status</Label>
                        <div className="d-flex align-items-center gap-3">
                          <label className="m-0 d-flex align-items-center gap-1">
                            <Input
                              type="radio"
                              name="subStatus"
                              checked={subForm.status === "Active"}
                              onChange={() =>
                                setSubForm((p) => ({ ...p, status: "Active" }))
                              }
                            />
                            <span>Active</span>
                          </label>
                          <label className="m-0 d-flex align-items-center gap-1">
                            <Input
                              type="radio"
                              name="subStatus"
                              checked={subForm.status === "Deactive"}
                              onChange={() =>
                                setSubForm((p) => ({
                                  ...p,
                                  status: "Deactive",
                                }))
                              }
                            />
                            <span>Deactive</span>
                          </label>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      color="secondary"
                      type="button"
                      onClick={() => setShowCreateSub(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      type="submit"
                      disabled={createSubLoading}
                    >
                      {createSubLoading ? "Saving..." : "Save Subcategory"}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* ===== Edit Subcategory ===== */}
      {mode === "subcategories" && editingSub && (
        <Row id="edit-sub-card" style={{ marginTop: 8 }}>
          <Col sm="12">
            <Card className="cs-card">
              <CardBody>
                <Form onSubmit={onUpdateSub}>
                  <Row>
                    <Col lg="4" md="6" sm="12">
                      <FormGroup>
                        <Label>Parent Category *</Label>
                        <Input
                          type="select"
                          required
                          value={editingSub.parentId}
                          onChange={(e) =>
                            setEditingSub((p) => ({
                              ...p,
                              parentId: e.target.value,
                            }))
                          }
                        >
                          <option value="">-- Select Category --</option>
                          {rows.map((c) => (
                            <option key={c?._id} value={c?._id}>
                              {c?.name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col lg="4" md="6" sm="12">
                      <FormGroup>
                        <Label>Subcategory Name</Label>
                        <Input
                          placeholder="Subcategory Name"
                          value={editingSub.name}
                          onChange={(e) =>
                            setEditingSub((p) => ({
                              ...p,
                              name: e.target.value,
                            }))
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col lg="4" md="6" sm="12">
                      <FormGroup>
                        <Label>Status</Label>
                        <div className="d-flex align-items-center gap-3">
                          <label className="m-0 d-flex align-items-center gap-1">
                            <Input
                              type="radio"
                              name="editSubStatus"
                              checked={editingSub.status === "Active"}
                              onChange={() =>
                                setEditingSub((p) => ({
                                  ...p,
                                  status: "Active",
                                }))
                              }
                            />
                            <span>Active</span>
                          </label>
                          <label className="m-0 d-flex align-items-center gap-1">
                            <Input
                              type="radio"
                              name="editSubStatus"
                              checked={editingSub.status === "Deactive"}
                              onChange={() =>
                                setEditingSub((p) => ({
                                  ...p,
                                  status: "Deactive",
                                }))
                              }
                            />
                            <span>Deactive</span>
                          </label>
                        </div>
                      </FormGroup>
                    </Col>
                    <Col lg="12" md="12" sm="12">
                      <FormGroup>
                        <Label>Subcategory Description</Label>
                        <Input
                          type="textarea"
                          rows={3}
                          placeholder="Description"
                          value={editingSub.description}
                          onChange={(e) =>
                            setEditingSub((p) => ({
                              ...p,
                              description: e.target.value,
                            }))
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      color="secondary"
                      type="button"
                      onClick={() => setEditingSub(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      type="submit"
                      disabled={editSubLoading}
                    >
                      {editSubLoading ? "Saving..." : "Update Subcategory"}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* ===== Add Product Section ===== */}
      {mode === "products" && showCreateProd && (
        <Row style={{ marginTop: 8 }}>
          <Col sm="12">
            <Card className="cs-card">
              <CardBody>
                <Form onSubmit={onCreateProductSection}>
                  <Row>
                    <Col lg="4" md="6" sm="12">
                      <FormGroup>
                        <Label>Section Name *</Label>
                        <Input
                          required
                          placeholder="Section Name"
                          value={prodForm.name}
                          onChange={(e) =>
                            setProdForm((p) => ({ ...p, name: e.target.value }))
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col lg="4" md="6" sm="12">
                      <FormGroup>
                        <Label>Status</Label>
                        <div className="d-flex align-items-center gap-3">
                          <label className="m-0 d-flex align-items-center gap-1">
                            <Input
                              type="radio"
                              name="prodStatus"
                              checked={prodForm.status === "Active"}
                              onChange={() =>
                                setProdForm((p) => ({ ...p, status: "Active" }))
                              }
                            />
                            <span>Active</span>
                          </label>
                          <label className="m-0 d-flex align-items-center gap-1">
                            <Input
                              type="radio"
                              name="prodStatus"
                              checked={prodForm.status === "Deactive"}
                              onChange={() =>
                                setProdForm((p) => ({
                                  ...p,
                                  status: "Deactive",
                                }))
                              }
                            />
                            <span>Deactive</span>
                          </label>
                        </div>
                      </FormGroup>
                    </Col>
                    <Col lg="12" md="12" sm="12">
                      <FormGroup>
                        <Label>Description</Label>
                        <Input
                          type="textarea"
                          rows={3}
                          placeholder="Description"
                          value={prodForm.description}
                          onChange={(e) =>
                            setProdForm((p) => ({
                              ...p,
                              description: e.target.value,
                            }))
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      color="secondary"
                      type="button"
                      onClick={() => setShowCreateProd(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      type="submit"
                      disabled={createProdLoading}
                    >
                      {createProdLoading ? "Saving..." : "Save Section"}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* ===== Edit Product Section ===== */}
      {mode === "products" && editingProdId && (
        <Row style={{ marginTop: 8 }}>
          <Col sm="12">
            <Card className="cs-card">
              <CardBody>
                <Form onSubmit={onUpdateProductSection}>
                  <Row>
                    <Col lg="6" md="6" sm="12">
                      <FormGroup>
                        <Label>Section Name</Label>
                        <Input
                          placeholder="Section Name"
                          value={editProd.name}
                          onChange={(e) =>
                            setEditProd((p) => ({ ...p, name: e.target.value }))
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col lg="6" md="6" sm="12">
                      <FormGroup>
                        <Label>Description</Label>
                        <Input
                          placeholder="Description"
                          value={editProd.description}
                          onChange={(e) =>
                            setEditProd((p) => ({
                              ...p,
                              description: e.target.value,
                            }))
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end gap-2 mt-2">
                    <Button
                      color="secondary"
                      type="button"
                      onClick={() => setEditingProdId(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      type="submit"
                      disabled={editProdLoading}
                    >
                      {editProdLoading ? "Saving..." : "Update Section"}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* ===== Tables ===== */}
      {mode === "categories" ? (
        <Row>
          <Col sm="12">
            <Card className="cs-card" style={{ padding: 8 }}>
              <div className="cs-table-wrap">
                <table className="cs-table">
                  <thead>
                    <tr>
                      <th>S.NO</th>
                      <th>CATEGORY NAME</th>
                      <th>STATUS</th>
                      <th>DESCRIPTION</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCategories.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          style={{ padding: 18, color: "#6b7280" }}
                        >
                          No categories found.
                        </td>
                      </tr>
                    ) : (
                      filteredCategories.map((r, i) => (
                        <tr key={r?._id || i}>
                          <td>{i + 1}</td>
                          <td>{r?.name}</td>
                          <td>{r?.status}</td>
                          <td style={{ textAlign: "left" }}>
                            {r?.description}
                          </td>
                          <td>
                            <div className="actions">
                              <span
                                className="chip chip-edit"
                                title="Edit"
                                onClick={() => startEditCategory(r?._id)}
                              >
                                <FaPencilAlt size={14} color="white" />
                              </span>
                              <span
                                className="chip chip-del"
                                title="Delete"
                                onClick={() => onDeleteCategory(r?._id)}
                              >
                                <Trash2 size={14} color="white" />
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </Col>
        </Row>
      ) : mode === "subcategories" ? (
        <Row>
          <Col sm="12">
            <Card className="cs-card" style={{ padding: 8 }}>
              <div className="cs-table-wrap">
                <table className="cs-table">
                  <thead>
                    <tr>
                      <th>S.NO</th>
                      <th>CATEGORY NAME</th>
                      <th>SUBCATEGORY NAME</th>
                      <th>STATUS</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubs.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          style={{ padding: 18, color: "#6b7280" }}
                        >
                          No subcategories found.
                        </td>
                      </tr>
                    ) : (
                      filteredSubs.map((r, i) => (
                        <tr key={`${r?.catId}-${r?.subId}-${i}`}>
                          <td>{i + 1}</td>
                          <td>{r?.catName}</td>
                          <td>{r?.subName}</td>
                          <td>{r?.status}</td>
                          <td>
                            <div className="actions">
                              <span
                                className="chip chip-edit"
                                title="Edit"
                                onClick={() => startEditSub(r)}
                              >
                                <FaPencilAlt size={14} color="white" />
                              </span>
                              <span
                                className="chip chip-del"
                                title="Delete"
                                onClick={() => onDeleteSub(r)}
                              >
                                <Trash2 size={14} color="white" />
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </Col>
        </Row>
      ) : (
        <Row>
          <Col sm="12">
            <Card className="cs-card" style={{ padding: 8 }}>
              <div className="cs-table-wrap">
                <table className="cs-table">
                  <thead>
                    <tr>
                      <th>S.NO</th>
                      <th>SECTION NAME</th>
                      <th>STATUS</th>
                      <th>DESCRIPTION</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          style={{ padding: 18, color: "#6b7280" }}
                        >
                          No product sections found.
                        </td>
                      </tr>
                    ) : (
                      filteredProducts.map((r, i) => (
                        <tr key={r?._id || i}>
                          <td>{i + 1}</td>
                          <td>{r?.name}</td>
                          <td>{r?.status}</td>
                          <td style={{ textAlign: "left" }}>
                            {r?.description}
                          </td>
                          <td>
                            <div className="actions">
                              <span
                                className="chip chip-edit"
                                title="Edit"
                                onClick={() => startEditProductSection(r?._id)}
                              >
                                <FaPencilAlt size={14} color="white" />
                              </span>
                              <span
                                className="chip chip-del"
                                title="Delete"
                                onClick={() => onDeleteProductSection(r?._id)}
                              >
                                <Trash2 size={14} color="white" />
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
}
